import React, { useState, useEffect } from 'react';
import { useNode } from '@craftjs/core';
import { useTheme } from '../../../contexts/ThemeContext';
import { Plus, Trash2, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const PricingBlock = ({ items, showQuantity, chargeVat, vatRate, currency, title, billingFrequency, showBillingToggle }) => {
  const { isDark } = useTheme();
  const [selectedBilling, setSelectedBilling] = useState(billingFrequency || 'annual');
  const { connectors: { connect, drag }, selected, actions: { setProp } } = useNode((state) => ({
    selected: state.events.selected
  }));

  const updateItem = (index, field, value) => {
    setProp(props => {
      const newItems = [...props.items];
      newItems[index] = { ...newItems[index], [field]: value };
      props.items = newItems;
    });
  };

  const addItem = () => {
    setProp(props => {
      props.items = [...props.items, { 
        name: 'New Item', 
        description: '', 
        monthlyPrice: 0, 
        annualPrice: 0, 
        quantity: 1, 
        optional: false,
        billingType: 'both' // 'monthly', 'annual', or 'both'
      }];
    });
  };

  const removeItem = (index) => {
    setProp(props => {
      props.items = props.items.filter((_, i) => i !== index);
    });
  };

  // Calculate totals based on selected billing frequency
  const getItemPrice = (item) => {
    if (item.billingType === 'monthly' || selectedBilling === 'monthly') {
      return item.monthlyPrice || item.price || 0;
    }
    return item.annualPrice || item.price || 0;
  };

  const subtotal = items.reduce((sum, item) => {
    if (item.optional && !item.selected) return sum;
    return sum + (getItemPrice(item) * (item.quantity || 1));
  }, 0);

  const vatAmount = chargeVat ? subtotal * (vatRate || 0.20) : 0;
  const total = subtotal + vatAmount;

  return (
    <div
      ref={(ref) => ref && connect(drag(ref))}
      className={`p-4 rounded-lg transition-all ${
        selected ? 'ring-2 ring-[#A2FF00]' : ''
      } ${isDark ? 'hover:bg-zinc-800/50' : 'hover:bg-gray-100'}`}
    >
      {/* Title */}
      {title && (
        <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {title}
        </h3>
      )}

      {/* Billing Toggle */}
      {showBillingToggle && (
        <div className="flex justify-center mb-4">
          <div className={`inline-flex rounded-lg p-1 ${isDark ? 'bg-zinc-800' : 'bg-gray-100'}`}>
            <button
              onClick={() => setSelectedBilling('monthly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                selectedBilling === 'monthly'
                  ? 'bg-[#A2FF00] text-black'
                  : isDark ? 'text-zinc-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setSelectedBilling('annual')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                selectedBilling === 'annual'
                  ? 'bg-[#A2FF00] text-black'
                  : isDark ? 'text-zinc-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Annual
              <span className={`ml-1 text-xs ${selectedBilling === 'annual' ? 'text-black/70' : isDark ? 'text-green-400' : 'text-green-600'}`}>
                Save 20%
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Pricing Table */}
      <div className={`rounded-lg border overflow-hidden ${isDark ? 'border-zinc-700' : 'border-gray-200'}`}>
        <table className="w-full">
          <thead className={isDark ? 'bg-zinc-800' : 'bg-gray-50'}>
            <tr>
              <th className={`text-left p-3 text-sm font-medium ${isDark ? 'text-zinc-300' : 'text-gray-600'}`}>Item</th>
              {showQuantity && (
                <th className={`text-center p-3 text-sm font-medium w-24 ${isDark ? 'text-zinc-300' : 'text-gray-600'}`}>Qty</th>
              )}
              <th className={`text-right p-3 text-sm font-medium w-32 ${isDark ? 'text-zinc-300' : 'text-gray-600'}`}>
                {selectedBilling === 'monthly' ? 'Monthly' : 'Annual'}
              </th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={index} className={`border-t ${isDark ? 'border-zinc-700' : 'border-gray-200'}`}>
                <td className="p-3">
                  <div className="flex items-start gap-2">
                    {item.optional && (
                      <button
                        onClick={() => updateItem(index, 'selected', !item.selected)}
                        className={`mt-1 w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${
                          item.selected
                            ? 'bg-[#A2FF00] border-[#A2FF00]'
                            : isDark ? 'border-zinc-600' : 'border-gray-300'
                        }`}
                      >
                        {item.selected && <Check className="h-3 w-3 text-black" />}
                      </button>
                    )}
                    <div className="flex-1">
                      <input
                        type="text"
                        value={item.name}
                        onChange={(e) => updateItem(index, 'name', e.target.value)}
                        className={`w-full bg-transparent font-medium outline-none ${isDark ? 'text-white' : 'text-gray-900'}`}
                        placeholder="Item name"
                      />
                      <input
                        type="text"
                        value={item.description || ''}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        className={`w-full bg-transparent text-sm outline-none ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}
                        placeholder="Description (optional)"
                      />
                      {/* Billing type indicator */}
                      {item.billingType && item.billingType !== 'both' && (
                        <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-gray-400'}`}>
                          ({item.billingType} only)
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                {showQuantity && (
                  <td className="p-3">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => updateItem(index, 'quantity', Math.max(1, (item.quantity || 1) - 1))}
                        className={`w-7 h-7 rounded flex items-center justify-center ${
                          isDark ? 'bg-zinc-700 hover:bg-zinc-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                        }`}
                      >
                        -
                      </button>
                      <span className={`w-8 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {item.quantity || 1}
                      </span>
                      <button
                        onClick={() => updateItem(index, 'quantity', (item.quantity || 1) + 1)}
                        className={`w-7 h-7 rounded flex items-center justify-center ${
                          isDark ? 'bg-zinc-700 hover:bg-zinc-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                        }`}
                      >
                        +
                      </button>
                    </div>
                  </td>
                )}
                <td className="p-3">
                  <div className="flex items-center justify-end gap-1">
                    <span className={isDark ? 'text-zinc-400' : 'text-gray-500'}>{currency}</span>
                    <input
                      type="number"
                      value={getItemPrice(item)}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        if (selectedBilling === 'monthly') {
                          updateItem(index, 'monthlyPrice', value);
                        } else {
                          updateItem(index, 'annualPrice', value);
                        }
                      }}
                      className={`w-20 bg-transparent text-right font-medium outline-none ${isDark ? 'text-white' : 'text-gray-900'}`}
                    />
                  </div>
                </td>
                <td className="p-3">
                  <button
                    onClick={() => removeItem(index)}
                    className={`p-1 rounded hover:bg-red-500/20 ${isDark ? 'text-zinc-500 hover:text-red-400' : 'text-gray-400 hover:text-red-500'}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Add Item Button */}
        <button
          onClick={addItem}
          className={`w-full p-3 flex items-center justify-center gap-2 border-t ${
            isDark ? 'border-zinc-700 text-zinc-400 hover:bg-zinc-800' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
          }`}
        >
          <Plus className="h-4 w-4" />
          Add Item
        </button>

        {/* Totals */}
        <div className={`border-t p-4 space-y-2 ${isDark ? 'border-zinc-700 bg-zinc-800/50' : 'border-gray-200 bg-gray-50'}`}>
          <div className="flex justify-between">
            <span className={isDark ? 'text-zinc-400' : 'text-gray-600'}>Subtotal</span>
            <span className={isDark ? 'text-white' : 'text-gray-900'}>{currency}{subtotal.toLocaleString()}</span>
          </div>
          {chargeVat && (
            <div className="flex justify-between">
              <span className={isDark ? 'text-zinc-400' : 'text-gray-600'}>VAT ({Math.round(vatRate * 100)}%)</span>
              <span className={isDark ? 'text-white' : 'text-gray-900'}>{currency}{vatAmount.toLocaleString()}</span>
            </div>
          )}
          <div className={`flex justify-between pt-2 border-t ${isDark ? 'border-zinc-700' : 'border-gray-300'}`}>
            <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Total {selectedBilling === 'monthly' ? '/ month' : '/ year'}
            </span>
            <span className={`text-xl font-bold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`}>
              {currency}{total.toLocaleString()}
            </span>
          </div>
          {selectedBilling === 'monthly' && (
            <div className={`text-xs text-right ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>
              Annual total: {currency}{(total * 12).toLocaleString()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

PricingBlock.craft = {
  displayName: 'Pricing Table',
  props: {
    title: 'Pricing',
    items: [
      { name: 'Membership Package', description: 'Full access to member benefits', monthlyPrice: 50, annualPrice: 500, quantity: 1, optional: false, billingType: 'both' },
      { name: 'Optional Add-on', description: 'Additional premium features', monthlyPrice: 10, annualPrice: 100, quantity: 1, optional: true, selected: false, billingType: 'both' }
    ],
    showQuantity: true,
    showBillingToggle: true,
    billingFrequency: 'annual',
    chargeVat: true,
    vatRate: 0.20,
    currency: '£'
  },
  related: {
    toolbar: PricingBlockSettings
  }
};

function PricingBlockSettings() {
  const { actions: { setProp }, props } = useNode((node) => ({
    props: node.data.props
  }));
  const { isDark } = useTheme();
  const [products, setProducts] = useState([]);

  // Fetch products from backend
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/products`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          setProducts(data);
        }
      } catch (err) {
        console.error('Failed to fetch products:', err);
      }
    };
    fetchProducts();
  }, []);

  const addFromProduct = (product) => {
    // Get variants if available
    const variants = product.variants || [];
    const monthlyVariant = variants.find(v => v.billing_frequency === 'monthly');
    const annualVariant = variants.find(v => v.billing_frequency === 'annual');
    
    setProp(props => {
      props.items = [...props.items, {
        product_id: product.product_id, // Store product reference for tracking
        name: product.name,
        description: product.description || '',
        monthlyPrice: monthlyVariant?.price_gbp || product.price_gbp || 0,
        annualPrice: annualVariant?.price_gbp || product.price_gbp || 0,
        monthlyVariantId: monthlyVariant?.variant_id || null,
        annualVariantId: annualVariant?.variant_id || null,
        quantity: 1,
        optional: false,
        billingType: product.billing_frequency === 'monthly' ? 'monthly' : 
                     product.billing_frequency === 'annual' ? 'annual' : 'both'
      }];
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
          Title
        </label>
        <input
          type="text"
          value={props.title}
          onChange={(e) => setProp(props => props.title = e.target.value)}
          className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300'}`}
        />
      </div>

      <div>
        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
          Currency
        </label>
        <select
          value={props.currency}
          onChange={(e) => setProp(props => props.currency = e.target.value)}
          className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300'}`}
        >
          <option value="£">£ GBP</option>
          <option value="$">$ USD</option>
          <option value="€">€ EUR</option>
        </select>
      </div>

      <div>
        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
          Default Billing
        </label>
        <div className="flex gap-2">
          {['monthly', 'annual'].map((freq) => (
            <button
              key={freq}
              onClick={() => setProp(props => props.billingFrequency = freq)}
              className={`flex-1 px-3 py-2 rounded-lg border capitalize ${
                props.billingFrequency === freq
                  ? 'bg-[#A2FF00] text-black border-[#A2FF00]'
                  : isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300'
              }`}
            >
              {freq}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="showBillingToggle"
          checked={props.showBillingToggle}
          onChange={(e) => setProp(props => props.showBillingToggle = e.target.checked)}
          className="w-4 h-4"
        />
        <label htmlFor="showBillingToggle" className={`text-sm ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
          Show Monthly/Annual toggle
        </label>
      </div>

      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="showQuantity"
          checked={props.showQuantity}
          onChange={(e) => setProp(props => props.showQuantity = e.target.checked)}
          className="w-4 h-4"
        />
        <label htmlFor="showQuantity" className={`text-sm ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
          Show quantity selector
        </label>
      </div>

      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="chargeVat"
          checked={props.chargeVat}
          onChange={(e) => setProp(props => props.chargeVat = e.target.checked)}
          className="w-4 h-4"
        />
        <label htmlFor="chargeVat" className={`text-sm ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
          Charge VAT
        </label>
      </div>

      {props.chargeVat && (
        <div>
          <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
            VAT Rate
          </label>
          <select
            value={props.vatRate}
            onChange={(e) => setProp(props => props.vatRate = parseFloat(e.target.value))}
            className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300'}`}
          >
            <option value="0.20">20%</option>
            <option value="0.10">10%</option>
            <option value="0.05">5%</option>
            <option value="0">0%</option>
          </select>
        </div>
      )}

      {/* Products Section - Always show */}
      <div>
        <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
          Add from Products
        </label>
        {products.length === 0 ? (
          <p className={`text-sm ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>
            No products configured. Add products in Admin → Products.
          </p>
        ) : (
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {products.map((product) => (
              <button
                key={product.product_id}
                onClick={() => addFromProduct(product)}
                className={`w-full text-left px-3 py-2 rounded-lg border flex justify-between items-center ${
                  isDark ? 'bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700' : 'bg-white border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium">{product.name}</div>
                  <div className={`text-xs ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {product.billing_frequency}
                  </div>
                </div>
                <span className={`ml-2 flex-shrink-0 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`}>
                  £{product.price_gbp}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export { PricingBlockSettings };
